import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActivityTimeline = ({ activities = [] }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return { icon: 'CheckCircle', color: 'text-success' };
      case 'failed':
        return { icon: 'XCircle', color: 'text-error' };
      case 'processing':
        return { icon: 'Clock', color: 'text-warning' };
      case 'pending':
        return { icon: 'Clock', color: 'text-text-secondary' };
      default:
        return { icon: 'Circle', color: 'text-text-secondary' };
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'failed':
        return 'Fallido';
      case 'processing':
        return 'Procesando';
      case 'pending':
        return 'Pendiente';
      default:
        return 'Desconocido';
    }
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(new Date(date));
  };

  const displayActivities = activities?.length > 0 ? activities : [];
  const hasRealData = activities?.length > 0;

  return (
    <div className="bg-surface border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-text-primary">
          {hasRealData ? 'Actividad Reciente' : 'Actividad Reciente (Sin datos)'}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.location.href = '/upload-history'}
          iconName="ExternalLink"
          iconPosition="right"
        >
          Ver Todo
        </Button>
      </div>
      
      {/* Real Data Status Indicator */}
      {!hasRealData && (
        <div className="mb-4 p-3 bg-info/10 border border-info/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="Info" className="text-info" size={16} />
            <p className="text-sm text-info">
              Mostrando datos reales de tus cargas. Sin actividad registrada aún.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {displayActivities?.map((activity, index) => {
          const statusConfig = getStatusIcon(activity?.status);
          
          return (
            <div key={activity?.id} className="relative">
              {/* Timeline line */}
              {index < displayActivities?.length - 1 && (
                <div className="absolute left-6 top-12 w-0.5 h-16 bg-border"></div>
              )}
              <div className="flex items-start space-x-4">
                {/* Status icon */}
                <div className={`w-12 h-12 rounded-full bg-surface border-2 border-border flex items-center justify-center ${statusConfig?.color}`}>
                  <Icon name={statusConfig?.icon} size={20} />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-text-primary mb-1">
                        {activity?.title}
                      </h4>
                      <p className="text-sm text-text-secondary mb-2">
                        {activity?.description}
                      </p>
                      
                      {/* Details */}
                      <div className="flex flex-wrap items-center gap-4 text-xs text-text-secondary mb-2">
                        <span className="flex items-center space-x-1">
                          <Icon name="Package" size={12} />
                          <span>Total: {activity?.details?.total}</span>
                        </span>
                        <span className="flex items-center space-x-1 text-success">
                          <Icon name="CheckCircle" size={12} />
                          <span>Exitosos: {activity?.details?.successful}</span>
                        </span>
                        {activity?.details?.failed > 0 && (
                          <span className="flex items-center space-x-1 text-error">
                            <Icon name="XCircle" size={12} />
                            <span>Fallidos: {activity?.details?.failed}</span>
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-text-secondary">
                          {formatDate(activity?.timestamp)}
                        </span>
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            activity?.status === 'completed' ? 'bg-success/10 text-success' :
                            activity?.status === 'failed' ? 'bg-error/10 text-error' :
                            activity?.status === 'processing'? 'bg-warning/10 text-warning' : 'bg-muted text-text-secondary'
                          }`}>
                            {getStatusText(activity?.status)}
                          </span>
                          
                          {activity?.status === 'failed' && (
                            <Button
                              variant="ghost"
                              size="xs"
                              iconName="RotateCcw"
                            >
                              Reintentar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {displayActivities?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Activity" size={48} className="mx-auto text-text-secondary mb-4" />
          <p className="text-text-secondary">Sin actividad reciente registrada</p>
          <p className="text-sm text-text-secondary mt-1">
            Los datos aparecerán aquí cuando comiences a cargar productos
          </p>
        </div>
      )}
    </div>
  );
};

export default ActivityTimeline;